import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Send, Loader2, AlertTriangle, FileText, Smile, Paperclip, X } from 'lucide-react'
import { useSendWAMessage } from '@/hooks/useWhatsApp'
import { waApi } from '@/lib/api-client'
import { toast } from 'sonner'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'

interface ChatInputProps {
  conversationId: number
  windowOpen: boolean
}

export function ChatInput({ conversationId, windowOpen }: ChatInputProps) {
  const [message, setMessage] = React.useState('')
  const [attachment, setAttachment] = React.useState<{
    file: File
    preview?: string
    uploading: boolean
    mediaId?: string
  } | null>(null)
  const [emojiOpen, setEmojiOpen] = React.useState(false)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const sendMessage = useSendWAMessage()

  // Auto-resize textarea
  React.useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [message])

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview)
      }
    }
  }, [attachment?.preview])

  // Handle emoji selection
  const handleEmojiSelect = React.useCallback((emoji: { native: string }) => {
    setMessage(prev => prev + emoji.native)
    setEmojiOpen(false)
    textareaRef.current?.focus()
  }, [])

  // Handle file selection
  const handleFileSelect = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input so same file can be selected again
    e.target.value = ''

    // Validate size (16MB)
    if (file.size > 16 * 1024 * 1024) {
      toast.error('File too large (max 16MB)')
      return
    }

    // Create preview for images
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined

    setAttachment({ file, preview, uploading: true })

    try {
      // Upload to Meta via our API
      const result = await waApi.conversations.uploadMedia(file)
      setAttachment(prev => prev ? { ...prev, uploading: false, mediaId: result.mediaId } : null)
    } catch (error) {
      toast.error('Failed to upload file')
      setAttachment(null)
    }
  }, [])

  // Remove attachment
  const removeAttachment = React.useCallback(() => {
    if (attachment?.preview) {
      URL.revokeObjectURL(attachment.preview)
    }
    setAttachment(null)
  }, [attachment])

  // Handle send
  const handleSend = React.useCallback(() => {
    if (!windowOpen) {
      toast.error('24-hour window expired. Please use a template.')
      return
    }

    // Send image with optional caption
    if (attachment?.mediaId) {
      const isImage = attachment.file.type.startsWith('image/')

      sendMessage.mutate(
        {
          conversationId,
          data: {
            type: isImage ? 'image' : 'document',
            mediaId: attachment.mediaId,
            mediaFileName: attachment.file.name,
            caption: message.trim() || undefined,
          },
        },
        {
          onSuccess: () => {
            setMessage('')
            setAttachment(null)
            textareaRef.current?.focus()
          },
        }
      )
      return
    }

    // Send text message
    const text = message.trim()
    if (!text) return

    sendMessage.mutate(
      {
        conversationId,
        data: { type: 'text', text },
      },
      {
        onSuccess: () => {
          setMessage('')
          textareaRef.current?.focus()
        },
      }
    )
  }, [message, conversationId, windowOpen, sendMessage, attachment])

  // Handle keyboard - Enter to send, Shift+Enter for new line
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const canSend = (message.trim().length > 0 || attachment?.mediaId) && !sendMessage.isPending

  return (
    <div className="flex-shrink-0 border-t border-[#e9edef] bg-[#f0f2f5] px-3 py-2.5">
      {/* Window warning */}
      {!windowOpen && (
        <div className="flex items-center gap-2 mb-2.5 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>24-hour window expired. Use a template to message.</span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto text-xs h-7 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            disabled
          >
            <FileText className="w-3 h-3 mr-1" />
            Choose template
          </Button>
        </div>
      )}

      {/* Attachment preview */}
      {attachment && (
        <div className="mb-2 relative inline-block">
          {attachment.preview ? (
            <div className="relative rounded-lg overflow-hidden border border-border">
              <img
                src={attachment.preview}
                alt="Attachment"
                className="max-h-[160px] max-w-[240px] object-cover"
              />
              {attachment.uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/50">
              <FileText className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-text-secondary truncate max-w-[200px]">
                {attachment.file.name}
              </span>
              {attachment.uploading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
          )}
          <button
            type="button"
            onClick={removeAttachment}
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center hover:bg-destructive/80"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Input area */}
      <TooltipProvider>
        <div className="flex items-end gap-1.5">
          {/* Emoji picker */}
          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground flex-shrink-0"
                    disabled={!windowOpen}
                  >
                    <Smile className="w-5 h-5" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top"><p>Emoji</p></TooltipContent>
            </Tooltip>
            <PopoverContent
              side="top"
              align="start"
              className="w-auto p-0 border-none shadow-xl"
              sideOffset={8}
            >
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme="dark"
                previewPosition="none"
                skinTonePosition="search"
                maxFrequentRows={2}
                perLine={8}
              />
            </PopoverContent>
          </Popover>

          {/* Attachment button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground flex-shrink-0"
                disabled={!windowOpen || !!attachment}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top"><p>Attach file</p></TooltipContent>
          </Tooltip>

          {/* Input field */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                attachment
                  ? 'Add a caption...'
                  : windowOpen
                    ? 'Type a message...'
                    : 'Window expired — use template'
              }
              disabled={!windowOpen || sendMessage.isPending}
              className={cn(
                'w-full min-h-[42px] max-h-[200px] py-2.5 px-3.5 text-[15px]',
                'bg-white rounded-lg border-0 resize-none text-[#111b21]',
                'placeholder:text-[#667781]/60',
                'focus:outline-none focus:ring-1 focus:ring-[#00a884]/30',
                'transition-[height] duration-100',
                !windowOpen && 'opacity-50 cursor-not-allowed'
              )}
              rows={1}
            />
          </div>

          {/* Send button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleSend}
                disabled={!canSend || !windowOpen}
                size="icon"
                className={cn(
                  'h-9 w-9 rounded-full flex-shrink-0 transition-colors',
                  canSend && windowOpen
                    ? 'bg-[#00a884] hover:bg-[#06cf9c] text-white'
                    : 'bg-[#e9edef] text-[#8696a0]'
                )}
              >
                {sendMessage.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top"><p>Send (Enter)</p></TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>
  )
}
